import { EMPTY_STRING } from '../AppConstants';
import {
  HostAppSettings,
  HostAppSettingName,
} from '../backend/office-js/HostAppSettings';
import { IsUnlimitedFileUploadMinutesAvailableForTenant } from './SupportedUnlimitedFileUploadMinutesTenants';

/*Currently Locale for Date is not supported, as checked oneNote and AudioRecorder, also provide
date in English only irrespective of locale, if required in future, localization support need to be added.*/
const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const weekDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function getDateString() : string {
  const currentDate = new Date();
  const hour =  currentDate.getHours();
  const minute = currentDate.getMinutes();
  const time = (hour > 12) ? (hour-12 + ':' + (minute < 10 ? '0' + minute : minute)  +' PM') :
                (hour + ':' + (minute < 10 ? '0'+minute : minute) +' AM');

  return time + " " + weekDays[currentDate.getDay()] + ", " + months[currentDate.getMonth()] + " " + currentDate.getDate() + ", " + currentDate.getFullYear();
}

/**
 * Wait will resolve the promise after waiting for timeperiod, provided as @Param {milliseconds}
 * If this wait should be cancelable before waiting timeperio, use @method {Cancelablewait}
 * @method wait
 * @param milliseconds
 * @returns Promise<void>
 */
export function wait(milliseconds: number): Promise<void> {
    return new Promise<void>((resolve, _) => {
        setTimeout(() => {
            resolve();
        }, milliseconds);
    });
}

/**
 * CancelableWait is extension of @method {wait}, where wait is cancelable,
 * using cancel method in @return, example
 * let waitObj = CancelableWait(2000);
 * waitObj.promise.then(() => {}); // to perform action after wait promise resolve.
 * waitObj.cancel(); // to clear wait request.
 * @method CancelableWait
 * @param milliseconds
 * @returns promise: Promise<void>, cancel: () => void
 */

export function CancelableWait(milliseconds: number): { promise: Promise<void>, cancel: () => void} {
  let timer: any;
  const promise =  new Promise<void>((resolve, _) => {
    timer =setTimeout(() => {
          resolve();
          timer = null;
      }, milliseconds);
  });
  return {
    promise: promise,
    cancel: () => {
      if (!isNullOrUndefined(timer)) {
        clearTimeout(timer);
      }
    }
  }
}

export function isEmptyString(str : string){
  return isNullOrUndefined(str) || str.length == 0;
}

export function isNullOrUndefined(object: any): boolean {
  return object === undefined || object === null || !object ;
}

export function ErrorStringFromException(e: any): string {
  if (isNullOrUndefined(e)) {
    return EMPTY_STRING;
  }
  let exceptionString: string = "Exception: " + JSON.stringify(e);
  const stringifyException = e.toString();
  const index = stringifyException.indexOf("[object ");

  if (index == -1) {
      if (e.name)
          exceptionString += ", name: " + e.name;
      if (e.code)
          exceptionString += ", code: " + e.code;
      if (e.message)
          exceptionString += ", message: " + e.message;
      if (e.stack)
          exceptionString += ", stack: " + e.stack;
  } else if (index != -1) {
    try {
      if (e.name)
          exceptionString += ", name: " + e.name;
      if (e.code)
          exceptionString += ", code: " + e.code;

      let moreDiagnostics: string =
      e?.message && e?.stack
        ? `${e.message}: ${e.stack}`
        : e?.stack
        ? e.stack
        : e?.message
        ? e.message
        : JSON.stringify(e);

      return moreDiagnostics ? exceptionString + " " + moreDiagnostics : exceptionString;

      } catch (err:any) {
        const error: string = `stringifyException error: ${err.message}: ${err.stack}`;
        return exceptionString + " " + error;
      }
  }

  return exceptionString;
}

export function PromiseWithTimeout<T>(promise: () => Promise<T>, timeout : number, promiseName : string) {
  let waitObj: any = CancelableWait(timeout);
  return Promise.race([
      promise().then(() => {
        if (!isNullOrUndefined(waitObj)) {
          waitObj.cancel();
        }
      }),
      waitObj.promise.then(() => {
        waitObj = null;
        return Promise.reject("The Promise: "+ promiseName +" timed out");
      }),
    ]);
}

export async function PromiseWithRetry<T>(promise: (attempt: number) => Promise<T>, maxRetryAllowed: number, delayInterval: number): Promise<T> {
  let lastError: any;
  for (let attempt = 0 ; attempt < maxRetryAllowed; attempt++) {
    try {
      if (attempt > 0) {
        await wait(delayInterval);
      }
      return await promise(attempt);
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError;
}

export function IsFileNameValidForOneDriveUpload(fileName: string): boolean {
  //Reference Documentation - https://support.microsoft.com/en-us/office/restrictions-and-limitations-in-onedrive-and-sharepoint-64883a5d-228e-48f5-b3d2-eb39e07630fa#invalidcharacters

  //Leading and trailing space in the file name is not allowed for Onedrive file upload.
  const leadingTrailingSpaceRegex = /^\s+|\s+$/g;
  const isLeadingOrTrailingSpaceInFileName = leadingTrailingSpaceRegex.test(fileName);
  if (isLeadingOrTrailingSpaceInFileName) {
    return false;
  }

  // forbidden characters \ / : * ? " < > | in the file name for Onedrive file upload
  const fileNameValidityRegex = /^[^\\/:\*\?"<>\|]+$/;
  return fileNameValidityRegex.test(fileName);
}

/**
 * To check whether unlimited file upload limit is available for current user
 * @returns true - For all consumer licensed users, For enterprise users if tenants is listed in IsUnlimitedFileUploadMinutesAvailableForTenant
 * false : For all other cases
 */
export function IsUnlimitedFileUploadMinutesAvailableForUser(): boolean {
  const isUnlimitedMinutesEnabled: boolean = HostAppSettings.IsSettingTrue(HostAppSettingName.UnlimitedMins);
  const isLicensedConsumerUser: boolean = HostAppSettings.IsSettingTrue(HostAppSettingName.IsLicensedUser);
  const tenantId: string = HostAppSettings.GetSettingAsString(HostAppSettingName.UserTenantId);

  if (isUnlimitedMinutesEnabled && (isLicensedConsumerUser || IsUnlimitedFileUploadMinutesAvailableForTenant(tenantId))) {
    return true;
  }

  return false;
}

export function IsCopilotLicensedUser(): boolean {
  return HostAppSettings.IsSettingTrue(HostAppSettingName.IsCopilotLicensedUser);
}

/* waitIfSystemInSleepState helps to wait for System to come out of sleep, when System in sleep
windows event like setInterval are not triggred and hence can be used to detect when system is out of sleep */
export async function waitIfSystemInSleepState() {
  return new Promise<void>((resolve, _) => {
    const interval = setInterval(() => {
        clearInterval(interval);
        resolve();
    }, 1000);
});
}

export async function GetImageBuffer(imagePath: string): Promise<string | ArrayBuffer | null> {
  return new Promise<string | ArrayBuffer | null>(function (resolve, reject) {
      const xmlHttpRequest = new XMLHttpRequest();
      const imageFileReader = new FileReader();

      // Once image is successfully downloaded, file reader will convert it to base64.
      xmlHttpRequest.addEventListener('load', () => {
          imageFileReader.readAsDataURL(xmlHttpRequest.response);
      });

      // when image download is failed, return null is ImageBuffer.
      xmlHttpRequest.addEventListener('error', () => {
          return reject();
      });

      // When the conversion is finished, return the ArrayBuffer read from filereader.
      imageFileReader.addEventListener('load', async () => {
          return resolve(imageFileReader.result);
      });

      // When the conversion is failed, return null.
      imageFileReader.addEventListener('error', async () => {
          return reject();
      });

      xmlHttpRequest.open('GET', imagePath);
      xmlHttpRequest.responseType = 'blob';
      xmlHttpRequest.send();
  });
}